import { TourTip } from '@/components/TourTip';
/**
 * ============================================================
 * KEJJA OS — src/pages/community/CommunityLayout.tsx
 * Rose accent. Top-level shell with just an Outlet.
 * ============================================================
 */

import { Outlet, useOutletContext } from 'react-router-dom';
import { Users2 } from 'lucide-react';
import { useMyHomes } from '@/hooks/useMe';
import { Skeleton } from '@/components/ui/skeleton';

export interface CommunityContext {
  homeId: string;
  currentUserId: string | undefined;
  homeName: string;
}

export function useCommunityContext(): CommunityContext {
  return useOutletContext<CommunityContext>();
}

export default function CommunityLayout() {
  const { data: homesData, isLoading } = useMyHomes();
  const first = homesData?.homes?.[0];
  const homeId = first?.home.id;
  const homeName = first?.home.name ?? '';

  if (isLoading) {
    return (
      <div className="space-y-4">
        
      <TourTip
        id="community-v1"
        title={"Chamas, fellowships, couples"}
        body={"Your family + other families, linked by a shared goal."}
        bullets={["Start a chama — everyone contributes, everyone sees the pot", "Couples group gives just the two of you a private space"]}
        accent="rose"
      />
      <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!homeId) {
    return <div className="text-sm text-muted-foreground">No home found. Create a home first.</div>;
  }

  const ctx: CommunityContext = {
    homeId,
    currentUserId: undefined,
    homeName,
  };

  return (
    <div className="space-y-5">
      <header className="border-b border-border pb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 flex items-center justify-center flex-shrink-0">
            <Users2 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-bold text-foreground leading-tight tracking-tight">
              Community
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              <span className="italic">Your people, your groups, your neighborhood.</span>
              {homeName && <span className="ml-2">· {homeName}</span>}
            </p>
          </div>
        </div>
      </header>

      <Outlet context={ctx} />
    </div>
  );
}
